import React, { useMemo } from 'react';
import { GradientText } from '../shared/GradientText';
import { IconTenLevelPlanProgress } from './ReferralDataIcons';

const LEVELS = [
  { level: 1, commission: '10', tier: 'bronze' },
  { level: 2, commission: '10', tier: 'bronze' },
  { level: 3, commission: '10', tier: 'bronze' },
  { level: 4, commission: '10', tier: 'silver' },
  { level: 5, commission: '10', tier: 'silver' },
  { level: 6, commission: '10', tier: 'silver' },
  { level: 7, commission: '10', tier: 'gold' },
  { level: 8, commission: '10', tier: 'gold' },
  { level: 9, commission: '10', tier: 'gold' },
  { level: 10, commission: '10', tier: 'gold' },
];

const TIER_STYLES = {
  bronze: {
    card: 'border-[#CD7F32]/35 bg-gradient-to-b from-[#CD7F32]/15 to-dark-secondary/60',
    cardActive: 'border-[#CD7F32]/55 shadow-[0_0_28px_rgba(205,127,50,0.14)]',
    ring: 'ring-1 ring-[#CD7F32]/25',
    level: 'text-[#E8B896]',
    bar: 'bg-gradient-to-r from-[#8B5A2B] via-[#CD7F32] to-[#E8B896]',
    dot: 'bg-[#CD7F32]',
  },
  silver: {
    card: 'border-[#9CA3AF]/35 bg-gradient-to-b from-[#C0C0C0]/12 to-dark-secondary/60',
    cardActive: 'border-[#D1D5DB]/55 shadow-[0_0_28px_rgba(192,192,192,0.12)]',
    ring: 'ring-1 ring-[#C0C0C0]/20',
    level: 'text-[#E5E7EB]',
    bar: 'bg-gradient-to-r from-[#6B7280] via-[#C0C0C0] to-[#F3F4F6]',
    dot: 'bg-[#C0C0C0]',
  },
  gold: {
    card: 'border-hash-gold/30 bg-gradient-to-b from-hash-gold/10 to-dark-secondary/60',
    cardActive: 'border-hash-gold/50 shadow-[0_0_32px_rgba(238,212,91,0.16)]',
    ring: 'ring-1 ring-hash-gold/25',
    level: 'text-hash-gold',
    bar: 'bg-gradient-to-r from-[#B8860B] via-[#EED45B] to-[#F7EC9C]',
    dot: 'bg-hash-gold',
  },
};

function ReferralTreeSection({ partnerLengths }) {
  const levelReportByLevel = useMemo(() => {
    const lengths = Array.isArray(partnerLengths) ? partnerLengths : [];
    return LEVELS.reduce((acc, { level }) => {
      acc[level] = {
        id: level,
        level,
        totalMember: Number(lengths[level - 1]) || 0,
      };
      return acc;
    }, {});
  }, [partnerLengths]);

  const maxMemberCount = useMemo(() => {
    return LEVELS.reduce((max, item) => {
      const reportRow = levelReportByLevel[item.level];
      const c = reportRow ? Number(reportRow.totalMember) || 0 : 0;
      return Math.max(max, c);
    }, 0);
  }, [levelReportByLevel]);

  const barScale = Math.max(maxMemberCount, 1);

  const totalMemberCount = useMemo(() => {
    return LEVELS.reduce((sum, item) => {
      const reportRow = levelReportByLevel[item.level];
      const count = reportRow ? Number(reportRow.totalMember) || 0 : 0;
      return sum + count;
    }, 0);
  }, [levelReportByLevel]);


  return (
    <div className="glass-card-gold-border rounded-2xl overflow-hidden mb-8">
      <div className="relative border-b border-hash-gold/15 bg-gradient-to-r from-dark-secondary/80 via-dark-secondary/40 to-dark-secondary/80 px-5 py-5 sm:px-6 sm:py-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(238,212,91,0.12),transparent)]" />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3 sm:items-center sm:gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-hash-gold/40 bg-hash-gold/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
              <IconTenLevelPlanProgress className="h-6 w-6 text-hash-gold" title="10 level plan progress" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white sm:text-xl">
                <GradientText variant="hash" className="text-lg font-bold sm:text-xl">
                  10 Level
                </GradientText>{' '}
                <span className="text-gray-200">Plan Progress</span>
              </h2>
              <p className="mt-1 max-w-xl text-xs leading-relaxed text-gray-500 sm:text-sm">
                Members attributed to your tree by depth. Bars compare each level to your busiest level.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <div className="inline-flex items-baseline gap-2 rounded-xl border border-hash-gold/20 bg-dark/60 px-4 py-2.5 backdrop-blur-sm">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Total team</span>
              <span className="text-2xl font-bold tabular-nums text-hash-gold text-shadow-gold">
                {totalMemberCount.toLocaleString()}
              </span>
              <span className="text-xs text-gray-500">members</span>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 sm:gap-3">
          {LEVELS.map((item) => {
            const reportRow = levelReportByLevel[item.level];
            const count = reportRow ? Number(reportRow.totalMember) || 0 : 0;
            const nftBuy = reportRow ? Number(reportRow.nftBuy) || 0 : 0;
            const rebuy = reportRow ? Number(reportRow.rebuy) || 0 : 0;
            const isActive = count > 0;
            const tier = TIER_STYLES[item.tier] || TIER_STYLES.bronze;
            const barPct = Math.min(100, (count / barScale) * 100);
            const showActivity = nftBuy > 0 || rebuy > 0;

            return (
              <div
                key={item.level}
                className={[
                  'group relative flex flex-col rounded-xl border p-3 transition-all duration-300 sm:p-3.5',
                  tier.card,
                  isActive ? `${tier.cardActive} ${tier.ring}` : 'opacity-[0.72]',
                ].join(' ')}
              >
                {isActive ? (
                  <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-white/[0.04] to-transparent" />
                ) : null}

                <div className="relative flex items-center justify-between gap-1">
                  <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-500 sm:text-[10px]">
                    L{item.level}
                  </span>
                  <span
                    className={[
                      'rounded-md px-1.5 py-0.5 text-[9px] font-bold tabular-nums sm:text-[10px]',
                      isActive ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-gray-500',
                    ].join(' ')}
                  >
                    {item.commission}%
                  </span>
                </div>

                <div
                  className={[
                    'relative mt-1 text-2xl font-black tabular-nums leading-none sm:text-3xl',
                    isActive ? tier.level : 'text-gray-500',
                  ].join(' ')}
                >
                  {count}
                </div>
                <div className="relative mt-0.5 text-[10px] text-gray-500 sm:text-xs">members</div>

                {showActivity ? (
                  <div className="relative mt-2 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] text-gray-500 sm:text-[10px]">
                    <span>
                      <span className="text-gray-600">Buy</span>{' '}
                      <span className="font-semibold text-gray-300 tabular-nums">{nftBuy}</span>
                    </span>
                    <span>
                      <span className="text-gray-600">Rebuy</span>{' '}
                      <span className="font-semibold text-gray-300 tabular-nums">{rebuy}</span>
                    </span>
                  </div>
                ) : (
                  <div className="relative mt-2 h-3 sm:h-4" />
                )}

                <div className="relative mt-auto pt-2">
                  <div className="h-1.5 overflow-hidden rounded-full bg-black/35 ring-1 ring-inset ring-white/[0.04]">
                    <div
                      className={['h-full rounded-full transition-all duration-500 ease-out', tier.bar].join(' ')}
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                </div>

                {isActive ? (
                  <div
                    className={['absolute right-2 top-2 h-1.5 w-1.5 rounded-full opacity-90', tier.dot].join(' ')}
                    aria-hidden
                  />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-white/5 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start sm:gap-3">
          </div>
          {totalMemberCount === 0 ? (
            <p className="text-center text-[11px] text-gray-600 sm:max-w-xs sm:text-right sm:text-xs">
              No affiliate activity yet. When your network buys or rebuys on-chain, counts show here by level.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ReferralTreeSection;
